import { useUser } from '../contexts/UserContext';
import { useQueue } from '../contexts/QueueContext';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Avatar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  Tooltip,
} from '@mui/material';
import Grid2 from '@mui/material/Grid';
import {
  IconArrowLeft,
  IconUserPlus,
  IconEdit,
  IconTrash,
  IconEye,
  IconUser,
  IconUserCheck,
  IconUserOff,
  IconSettings,
  IconUsersGroup,
  IconHeadset,
  IconSearch,
  IconFilter
} from '@tabler/icons-react';
import { User } from '../models/User';
import { userAPI } from '../services/api';

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const userContext = useUser();
  const queueContext = useQueue();
  const { users }: { users: User[] } = userContext;
  const { loading, error, fetchUsers, createUser, updateUser, deleteUser } = userContext;
  const { queues } = queueContext;
  const [editingUser, setEditingUser] = useState<any>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [saveError, setSaveError] = useState('');

  const fetchLocations = async () => {
    try {
      const response = await userAPI.getLocations();
      if (response.data?.success) {
        setLocations(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      navigate('/login');
      return;
    }
    
    fetchUsers();
    queueContext.fetchQueues();
    fetchLocations();
  }, []);



  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'agent',
    department: '',
    password: '',
    queueIds: [] as string[],
    locationId: '',
    sipExtension: '',
    sipPassword: ''
  });

  const [locations, setLocations] = useState<any[]>([]);

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return IconSettings;
      case 'supervisor': return IconUsersGroup;
      case 'agent': return IconHeadset;
      default: return IconUser;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return '#F44336';
      case 'supervisor': return '#FF9800';
      case 'agent': return '#4CAF50';
      default: return '#666';
    }
  };

  const getRoleText = (role: string) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'supervisor': return 'Supervisor';
      case 'agent': return 'Temsilci';
      default: return 'Kullanıcı';
    }
  };

  const getStatusText = (isActive: boolean | undefined) => {
    return isActive ? 'Aktif' : 'Pasif';
  };

  const getStatusColor = (isActive: boolean | undefined) => {
    return isActive ? '#4CAF50' : '#F44336';
  };

  const getQueueName = (queueId: any) => {
    if (typeof queueId === 'object' && queueId !== null && 'name' in queueId) {
      return queueId.name;
    }
    return typeof queueId === 'string' ? queueId : 'Bilinmiyor';
  };

  const filteredUsers = users.filter(user => {
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    const matchesSearch = fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const handleAddUser = () => {
    setEditingUser(null);
    setSaveError('');
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: 'agent',
      department: '',
      password: '',
      queueIds: [],
      locationId: '',
      sipExtension: '',
      sipPassword: ''
    });
    setOpenDialog(true);
  };

  const handleEditUser = (user: any) => {
    setEditingUser(user);
    setSaveError('');
    
    // Extract IDs from populated fields
    let locationId = '';
    if (user.locationId) {
      locationId = typeof user.locationId === 'object' ? user.locationId._id : user.locationId;
    }

    let queueIds: string[] = [];
    if (user.queueIds && Array.isArray(user.queueIds)) {
      queueIds = user.queueIds.map((q: any) => typeof q === 'object' ? q._id : q);
    }

    setFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email,
      phone: user.phone,
      role: user.role,
      department: user.department,
      password: '',
      queueIds: queueIds,
      locationId: locationId,
      sipExtension: user.sipExtension || '',
      sipPassword: user.sipPassword || ''
    });
    setOpenDialog(true);
  };

  const handleSaveUser = async () => {
    setSaveError('');

    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
      setSaveError('Ad, soyad ve e-posta zorunludur.');
      return;
    }

    if (!editingUser && formData.password.length < 8) {
      setSaveError('Şifre en az 8 karakter olmalıdır.');
      return;
    }

    const payload = {
      ...formData,
      phone: formData.phone || undefined,
      department: formData.department || undefined,
      locationId: formData.locationId || undefined,
      sipExtension: formData.sipExtension || undefined,
      sipPassword: formData.sipPassword || undefined,
      queueIds: formData.queueIds.filter(Boolean)
    };

    if (editingUser && !payload.password) {
      delete (payload as any).password;
    }

    const savedUser = editingUser
      ? await updateUser(editingUser._id, payload)
      : await createUser(payload);

    if (!savedUser) {
      setSaveError(error || 'Kullanıcı kaydedilemedi. Bilgileri kontrol edip tekrar deneyin.');
      return;
    }

    await fetchUsers();
    setOpenDialog(false);
  };

  const handleDeleteUser = async (userId: string) => {
    const user = users.find((item) => item._id === userId);
    const label = user
      ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
      : 'bu kullanıcı';

    const confirmed = window.confirm(`${label} hesabını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`);
    if (!confirmed) return;

    await deleteUser(userId);
    await fetchUsers();
  };

  const handleToggleUserActive = async (user: User) => {
    const nextIsActive = !user.isActive;
    const actionText = nextIsActive ? 'aktifleştirmek' : 'pasife almak';

    if (currentUser?._id === user._id && !nextIsActive) {
      alert('Kendi hesabınızı pasife alamazsınız.');
      return;
    }

    const confirmed = window.confirm(`${user.email} hesabını ${actionText} istediğinize emin misiniz?`);
    if (!confirmed) return;

    const updatedUser = await updateUser(user._id, { isActive: nextIsActive } as Partial<User>);
    if (!updatedUser) {
      alert('Hesap durumu güncellenemedi.');
      return;
    }

    await fetchUsers();
  };

  const approveUser = async (userId: string) => {
    try {
      const response = await userAPI.approve(userId);
      if (response.data && response.data.success) {
        await fetchUsers();
        alert('Kullanıcı başarıyla onaylandı.');
      } else {
        throw new Error(response.data?.message || 'Kullanıcı onaylanamadı.');
      }
    } catch (error) {
      console.error('Kullanıcı onaylama hatası:', error);
      alert('Kullanıcı onaylanırken bir hata oluştu.');
    }
  };

  const pendingUsers = users.filter(user => user.pendingApproval);

  // Determine current user
  const currentUser = userContext.authUser;

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundImage: 'url(./bg.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      p: 3,
      overflowY: 'auto'
    }}>
      {/* Geri İkonu */}
      <Box sx={{ mb: 3 }}>
        <Box 
          sx={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            p: 1,
            borderRadius: 2,
            backgroundColor: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
              transform: 'translateX(-2px)'
            }
          }}
          onClick={() => window.history.back()}
        >
          <IconArrowLeft size={24} color="white" />
        </Box>
      </Box>

      {/* Ana İçerik */}
      <Box sx={{ px: 10, height: 'calc(100vh - 80px)', overflowY: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="700" color="white" sx={{ fontFamily: 'Monaco, monospace', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
            Kullanıcı Yönetimi
          </Typography>
          {currentUser && (
            <Chip label={`${currentUser.role?.toUpperCase()} - ${currentUser.email}`} size="small" sx={{ bgcolor: '#ffffff44', color: '#fff', ml: 2 }} />
          )}
          <Button
            variant="contained"
            startIcon={<IconUserPlus size={20} />}
            onClick={handleAddUser}
            sx={{
              bgcolor: '#1976D2',
              '&:hover': { bgcolor: '#1565C0' },
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontFamily: 'Monaco, monospace',
              fontWeight: 600
            }}
          >
            Yeni Kullanıcı
          </Button>
        </Box>

        {/* Filtreler */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '1px solid #E0E0E0', background: 'white' }}>
          <CardContent sx={{ p: 3 }}>
            <Grid2 container spacing={2} alignItems="center">
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  placeholder="Kullanıcı ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <IconSearch size={20} color="#666" style={{ marginRight: 8 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontFamily: 'Monaco, monospace'
                    }
                  }}
                />
              </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Rol Filtresi</InputLabel>
                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  label="Rol Filtresi"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">Tüm Roller</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="supervisor">Supervisor</MenuItem>
                  <MenuItem value="agent">Temsilci</MenuItem>
                </Select>
              </FormControl>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={`Toplam: ${users.length}`}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`Aktif: ${users.filter(u => u.isActive === true).length}`}
                  color="success"
                  variant="outlined"
                />
              </Box>
            </Grid2>
          </Grid2>
        </CardContent>
      </Card>

        {/* Kullanıcı Tablosu */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '1px solid #E0E0E0', background: 'white' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#F5F5F5' }}>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Monaco, monospace' }}>Kullanıcı</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Monaco, monospace' }}>Rol</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Monaco, monospace' }}>Extension</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Monaco, monospace' }}>Lokasyon</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Monaco, monospace' }}>Kuyruklar</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Monaco, monospace' }}>Departman</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Monaco, monospace' }}>Durum</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Monaco, monospace' }}>Son Giriş</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Monaco, monospace' }}>İşlemler</TableCell>
                </TableRow>
              </TableHead>
            <TableBody>
              {filteredUsers.map((user) => {
                const RoleIcon = getRoleIcon(user.role);
                return (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#1976D2', width: 40, height: 40 }}>
                          {`${user.firstName || ''} ${user.lastName || ''}`.trim().split(' ').map(n => n[0]).join('') || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="500" color="#2C3E50" sx={{ fontFamily: 'Monaco, monospace' }}>
                            {`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Bilinmeyen'}
                          </Typography>
                          <Typography variant="body2" color="#666" sx={{ fontFamily: 'Monaco, monospace' }}>
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<RoleIcon size={16} color="white" />}
                        label={getRoleText(user.role)}
                        sx={{
                          bgcolor: getRoleColor(user.role),
                          color: 'white',
                          fontWeight: 500,
                          fontFamily: 'Monaco, monospace'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {user.sipExtension ? (
                        <Chip
                          label={`📞 ${user.sipExtension}`}
                          size="small"
                          sx={{
                            bgcolor: '#E8F5E9',
                            color: '#2E7D32',
                            fontWeight: 600,
                            fontFamily: 'Monaco, monospace'
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="#999" sx={{ fontFamily: 'Monaco, monospace' }}>
                          Atanmamış
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#666" sx={{ fontFamily: 'Inter, sans-serif' }}>
                        {user.locationId ? (
                          locations.find(loc => loc._id === user.locationId)?.name || 'Bilinmiyor'
                        ) : (
                          <Typography variant="caption" color="text.secondary">Atanmamış</Typography>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {user.queueIds && user.queueIds.length > 0 ? (
                          user.queueIds.map((queueId: string) => {
                            const queue = queues.find(q => q._id === queueId);
                            if (!queue) return null;
                            const isActive = queue.status === 'active';
                            return (
                              <Chip
                                key={queueId}
                                label={getQueueName(queueId)}
                                size="small"
                                icon={<span style={{ color: isActive ? '#4CAF50' : '#9E9E9E', fontSize: '12px' }}>●</span>}
                                sx={{
                                  bgcolor: isActive ? '#E3F2FD' : '#F5F5F5',
                                  color: isActive ? '#1976D2' : '#9E9E9E',
                                  fontWeight: 500,
                                  fontFamily: 'Monaco, monospace'
                                }}
                              />
                            );
                          })
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Monaco, monospace' }}>
                            Kuyruk atanmamış
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#666" sx={{ fontFamily: 'Inter, sans-serif' }}>
                        {user.department}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(user.isActive)}
                        sx={{
                          bgcolor: getStatusColor(user.isActive),
                          color: 'white',
                          fontWeight: 500,
                          fontFamily: 'Inter, sans-serif'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#666" sx={{ fontFamily: 'Monaco, monospace' }}>
                        {user.lastLogin}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Düzenle">
                          <IconButton
                            size="small"
                            onClick={() => handleEditUser(user)}
                            sx={{ color: '#1976D2' }}
                          >
                            <IconEdit size={20} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={user.isActive ? 'Pasife al' : 'Aktifleştir'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleUserActive(user)}
                              disabled={currentUser?._id === user._id && user.isActive}
                              sx={{ color: user.isActive ? '#FF9800' : '#4CAF50' }}
                            >
                              {user.isActive ? <IconUserOff size={20} /> : <IconUserCheck size={20} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Sil">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteUser(user._id)}
                            sx={{ color: '#F44336' }}
                          >
                            <IconTrash size={20} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Bekleyen Kullanıcılar Tablosu */}
      {(() => {
        if (!currentUser) {
          return (
            <Typography variant="h6" sx={{ marginBottom: '16px', color: 'white' }}>
              Kullanıcı bilgisi yükleniyor veya tanımlı değil.
            </Typography>
          );
        }

        if (!(currentUser.role === 'admin' || currentUser.role === 'supervisor')) {
          return (
            <Typography variant="h6" sx={{ marginBottom: '16px', color: 'white' }}>
              Bu sayfa sadece yönetici veya supervisor yetkisine sahip kullanıcılar içindir.
            </Typography>
          );
        }

        return (
          <>
            <Typography variant="h6" sx={{ marginBottom: '16px', color: 'white' }}>
              Onay Bekleyen Kullanıcılar
            </Typography>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '1px solid #E0E0E0', background: 'white', mb: 3 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F5F5F5' }}>
                      <TableCell sx={{ fontWeight: 600, fontFamily: 'Monaco, monospace' }}>Kullanıcı</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontFamily: 'Monaco, monospace' }}>E-posta</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontFamily: 'Monaco, monospace' }}>Rol</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontFamily: 'Monaco, monospace' }}>Onayla</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center' }}>Onay bekleyen kullanıcı yok</TableCell>
                      </TableRow>
                    ) : (
                      pendingUsers.map(user => (
                      <TableRow key={user._id}>
                        <TableCell>{user.firstName} {user.lastName}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{getRoleText(user.role)}</TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => approveUser(user._id)}
                          >
                            Onayla
                          </Button>
                        </TableCell>
                      </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </>
        );
      })()}
        </Box>

      {/* Kullanıcı Ekleme/Düzenleme Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
          {editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {saveError && (
              <Typography sx={{ color: '#d32f2f', fontSize: '0.9rem', fontWeight: 600 }}>
                {saveError}
              </Typography>
            )}
            <TextField
              fullWidth
              label="Ad"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <TextField
              fullWidth
              label="Soyad"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <TextField
              fullWidth
              label="E-posta"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <TextField
              fullWidth
              label="Telefon"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <FormControl fullWidth>
              <InputLabel>Rol</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                label="Rol"
              >
                <MenuItem value="agent">Temsilci</MenuItem>
                <MenuItem value="supervisor">Supervisor</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Lokasyon</InputLabel>
              <Select
                value={formData.locationId}
                onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                label="Lokasyon"
              >
                <MenuItem value="">Seçiniz</MenuItem>
                {locations.map(location => (
                  <MenuItem key={location._id} value={location._id}>
                    {location.name} - {location.city}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Departman"
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            {formData.role === 'agent' && (
              <FormControl fullWidth>
                <InputLabel>Atanacak Kuyruklar</InputLabel>
                <Select
                  multiple
                  value={formData.queueIds}
                  onChange={(e) => setFormData({ ...formData, queueIds: e.target.value as string[] })}
                  label="Atanacak Kuyruklar"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((queueId) => {
                        const queue = queues.find(q => q._id === queueId);
                        if (!queue) return null;
                        const isActive = queue.status === 'active';
                        return (
                          <Chip
                            key={queueId}
                            label={queue.name}
                            size="small"
                            icon={<span style={{ color: isActive ? '#4CAF50' : '#9E9E9E', fontSize: '10px' }}>●</span>}
                            sx={{
                              bgcolor: isActive ? '#E3F2FD' : '#F5F5F5',
                              color: isActive ? '#1976D2' : '#9E9E9E'
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {queues
                    .filter(queue => queue.status === 'active')
                    .map(queue => (
                      <MenuItem key={queue._id} value={queue._id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span style={{ color: '#4CAF50', fontSize: '12px' }}>●</span>
                          {queue.name}
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
            <TextField
              fullWidth
              label={editingUser ? "Yeni Şifre (boş bırakılırsa değişmez)" : "Şifre"}
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            
            {/* SIP Extension Ayarları */}
            <Box sx={{ 
              bgcolor: '#F5F5F5', 
              p: 2, 
              borderRadius: 2,
              border: '1px solid #E0E0E0'
            }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1976D2' }}>
                📞 SIP Extension Ayarları (FreePBX)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Extension Numarası"
                  placeholder="örn: 1001"
                  value={formData.sipExtension}
                  onChange={(e) => setFormData({ ...formData, sipExtension: e.target.value })}
                  sx={{ fontFamily: 'Inter, sans-serif', bgcolor: 'white' }}
                  helperText="Agent'ın FreePBX extension numarası"
                />
                <TextField
                  fullWidth
                  label="SIP Şifresi"
                  type="password"
                  placeholder="SIP şifresi"
                  value={formData.sipPassword}
                  onChange={(e) => setFormData({ ...formData, sipPassword: e.target.value })}
                  sx={{ fontFamily: 'Inter, sans-serif', bgcolor: 'white' }}
                  helperText="FreePBX'teki extension şifresi"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ fontFamily: 'Inter, sans-serif' }}>
            İptal
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            sx={{
              bgcolor: '#1976D2',
              '&:hover': { bgcolor: '#1565C0' },
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600
            }}
          >
            {editingUser ? 'Güncelle' : 'Ekle'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
